// workOrdersApi.ts: work orders, which are runs, picks into Assembly, units and their
// completion. Every API-shape assumption for /work-orders lives here, read off
// the S2 contract (.plans/erp-expansion/22-work-orders-api-contract.md).
//
// Money fields (`*_cents`) are ABSENT unless the caller holds can_financial
// (stripCostFields, admin does not bypass), so they are optional, and a page
// decides whether to show a cost column by whether the field arrived.

import { useApi } from '../apiHelpers';
import type { CameFrom } from '../Parts/Pulls/pullsApi';
import { formatCents } from '../Procurement/poApi';

export { formatCents };

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export type WorkOrderStatus =
  | 'suggested'
  | 'draft'
  | 'released'
  | 'in_progress'
  | 'complete'
  | 'closed'
  | 'on_hold'
  | 'cancelled';

export type UnitStatus = 'in_progress' | 'complete' | 'flagged';

// `count` and `bulk` are no longer written, but rows completed that way can still be read.
// `repair`: the drone was flagged off the run, then finished when its flag was cleared.
export type CompletionSource = 'count' | 'bulk' | 'scan' | 'commissioned' | 'repair';

export interface WorkOrderRow {
  id: string;
  number: string;
  status: WorkOrderStatus;
  output_part_id: number;
  /** The drone serial prefix; null when the product is not a drone (a charger run). */
  model_code: string | null;
  model_name: string | null;
  variant: string | null;
  quantity: number;
  completed_count: number;
  bound_count: number;
  /** Drones the run still needs: quantity less the units it has. */
  units_to_go?: number;
  planned_date: string | null;
  source: 'manual' | 'sales_order' | string;
  sales_order_id: string | number | null;
  so_number: string | null;
  created_at: string;
  released_at: string | null;
  closed_at: string | null;
  on_hold: boolean;
  /** Pick requests from the line still waiting for a box. */
  open_pick_requests?: number;
}

/** The balance maths behind a suggestion, from the `suggested` event's data. */
export interface Suggestion {
  line_quantity: number;
  available: number;
  committed: number;
  covered: number;
  balance: number;
}

/**
 * An inbox row. The contract names `sales_order`, `line` and `suggestion` but
 * not their inner fields; the inner shapes below are the fields this page
 * reads, every one optional so an absent field renders as nothing.
 */
export interface InboxRow extends WorkOrderRow {
  sales_order?: {
    id?: string | number;
    so_number?: string | null;
    customer_name?: string | null;
    delivery_date?: string | null;
  } | null;
  line?: { id?: string | number; quantity?: number; variant?: string | null } | null;
  suggestion?: Partial<Suggestion> | null;
}

export interface Assembly {
  id: number | string;
  name: string;
}

export interface WorkOrder extends WorkOrderRow {
  notes: string | null;
  bom_id: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  released_by_name: string | null;
  closed_by_name: string | null;
  cancelled_at: string | null;
  cancelled_by_name: string | null;
  assembly: Assembly | null;
}

export interface WorkOrderBom {
  id: string;
  label: string | null;
  variants: string[];
  labor_minutes: string | number | null;
  overhead_percent: string | number | null;
  labor_rate_cents?: number | null;
}

/** One entry of a unit's shortfall: a traced line not scanned, or a bulk line short. */
export interface ShortfallEntry {
  line_id: number;
  target_key: string;
  short?: number;
  note?: string;
}

export interface Unit {
  id: string;
  position: number;
  status: UnitStatus;
  serial: string | null;
  core_hex: string | null;
  bound_at: string | null;
  completed_at: string | null;
  completed_by_name: string | null;
  completion_source: CompletionSource | null;
  shortfall: ShortfallEntry[] | null;
  flagged_at?: string | null;
  flagged_by_name?: string | null;
  /** The reason given at the station's Flag key, when the API sends it. */
  flag_reason?: string | null;
  materials_cents?: number | null;
  labor_cents?: number | null;
  overhead_cents?: number | null;
  unit_cost_cents?: number | null;
}

/** One item still in a box. `on_this_line`: the part the line or run needs. */
export interface BoxItem {
  /** Present on Already picked rows; the pick list's box contents carry none. */
  receipt_item_id?: string;
  sku: string;
  display_name: string | null;
  quantity_remaining: number;
  on_this_line: boolean;
}

export interface PickBox {
  receipt_item_id: string | number;
  barcode: string;
  location_name: string | null;
  section_code?: string | null;
  area_name?: string | null;
  warehouse_name?: string | null;
  location_path?: string;
  quantity_remaining: number;
  received_at: string | null;
  sku: string | null;
  display_name: string | null;
  /** Everything still in the box: a pick moves the whole box. */
  items: BoxItem[];
}

/** A run's share of a pool line: what it still needs, oldest run first. */
export interface PoolRunNeed {
  work_order_id: string;
  number: string;
  needed: number;
}

/** An open request from a station, on whichever run asked. */
export interface PoolRequest {
  id: string;
  work_order_id: string;
  number: string;
  unit_position: number | null;
  quantity: number;
  requested_by_name: string | null;
  requested_at: string;
}

/** One part the counted runs still need, summed across them, against Assembly. */
export interface PoolLine {
  /** `f:<family id>` or `i:<item id>`. */
  key: string;
  target_name: string;
  traced: boolean;
  needed: number;
  in_assembly: number;
  to_move: number;
  runs: PoolRunNeed[];
  /** Shelf boxes holding the part; never the Assembly, never a legacy box. */
  boxes: PickBox[];
  requests: PoolRequest[];
}

/** A box in Assembly, with everything still in it. */
export interface PoolPickedBox {
  barcode: string;
  location_name: string | null;
  quantity_remaining: number;
  picked_at: string | null;
  picked_by_name: string | null;
  items: BoxItem[];
  /** The section it was picked from, when one still-active section holds the whole story. */
  came_from: CameFrom | null;
}

/** `GET /assembly/pick-list`: what needs to move to Assembly right now. */
export interface AssemblyPickList {
  assembly: Assembly | null;
  lines: PoolLine[];
  picked: PoolPickedBox[];
  open_requests: number;
}

export type CoverageStatus = 'covered' | 'short' | 'no_bom' | 'not_counted';

export interface CoverageLine {
  key: string;
  target_name: string;
  needed: number;
  covered: number;
  short: number;
}

/** `GET /work-orders/:id/coverage`: this run's claim on the pool, oldest run first. */
export interface Coverage {
  status: CoverageStatus;
  lines: CoverageLine[];
}

/** A box asked for from the line when a drone came up short (`POST /:id/pick-requests`). */
export interface PickRequest {
  id: string;
  /** Null once the parts list line is gone; `target_name` keeps the request readable. */
  bom_line_id: string | number | null;
  target_name?: string | null;
  unit_id: string | null;
  quantity: number;
  requested_by_name?: string | null;
  requested_at: string;
  filled_at: string | null;
  cancelled_at: string | null;
  cancelled_by_name?: string | null;
}

export interface PickRequestInput {
  unit_id?: string;
  lines: { line_id: number; quantity: number }[];
  operator_id?: string;
}

/** A slot key: an item kind key from the run's parts list (`AP`, `PWR`, `RPI`...). */
export type FloorSlot = string;

/** A run on the floor (released or in progress), for the station's run choice. */
export interface FloorRun {
  id: string;
  number: string;
  status: WorkOrderStatus;
  model_code: string | null;
  model_name: string | null;
  variant: string | null;
  quantity: number;
  completed_count: number;
  units_to_go: number;
  /** The slots this run's parts list traces. The station reads `GET /scanner/slots` instead. */
  slots: FloorSlot[];
  open_pick_requests?: number;
}

/**
 * What a traced line has on the drone. The contract names `component` without
 * its fields; a board reads by its UID and an instance by its sticker, so both
 * the object and a bare string are accepted and read by `componentReading`.
 */
export type CheckComponent =
  | string
  | { source?: 'board' | 'instance'; label?: string | null; value?: string | null; display_name?: string | null }
  | null;

export interface UnitCheckTraced {
  line_id: number;
  target_name: string;
  slot: FloorSlot | null;
  fitted: boolean;
  component: CheckComponent;
  /** Present when not fitted: how many more, and what Assembly holds to scan. */
  need?: number;
  in_assembly?: number;
}

export interface UnitCheckShort {
  line_id: number;
  target_name: string;
  need: number;
  in_assembly: number;
  /** What to ask for in a pick request, when the API sends it. */
  quantity?: number;
}

/** `GET /:id/units/by-serial/:serial/check`: may this drone be marked built. */
export interface UnitCheck {
  unit: Unit;
  ready: boolean;
  traced: UnitCheckTraced[];
  short: UnitCheckShort[];
}

export interface WorkOrderEvent {
  id: string | number;
  event: string;
  occurred_at: string;
  actor_name: string | null;
  unit_position: number | null;
  data: Record<string, unknown> | null;
}

export interface WorkOrderDetail {
  order: WorkOrder;
  bom: WorkOrderBom | null;
  units: Unit[];
  events: WorkOrderEvent[];
}

export interface PickMove {
  receipt_item_id: string | number;
  barcode: string;
  sku: string | null;
  display_name?: string | null;
  quantity: number;
  from: string;
  to: string;
}

export interface BoxEmptyResult {
  zeroed: { receipt_item_id: string | number; barcode: string; sku: string | null; quantity: number; location: string }[];
}

export interface PickResult {
  moved: PickMove[];
  assembly: Assembly;
}

/** `work_order_unit` on GET /scanner/assembly/:serial: the run the drone is bound to, open or not. */
export interface DroneWorkOrderUnit {
  work_order_id: string;
  number: string;
  position: number;
}

/** The unit whose run still owns the drone's parts: what a commit with no run refuses on. */
export interface LiveWorkOrderUnit extends DroneWorkOrderUnit {
  flagged: boolean;
}

export interface CloseReportLine {
  line_id: number;
  target_key: string;
  target_name: string;
  traced: boolean;
  quantity_per: string | number;
  required: number;
  consumed: number;
  unsourced: number;
  remaining_in_assembly: number;
  short: number;
  /** `consumed` split by whether the drone was finished or flagged off the run, when sent. */
  consumed_completed?: number;
  consumed_flagged?: number;
}

/** A drone flagged off the run, as the close report lists it. */
export interface FlaggedUnit {
  position: number;
  serial: string | null;
  flagged_at: string | null;
  flag_reason?: string | null;
  reason?: string | null;
}

export interface CloseReport {
  order: WorkOrderRow;
  lines: CloseReportLine[];
  units: { quantity: number; completed: number; bound: number; to_go: number; flagged?: FlaggedUnit[] };
  materials_total_cents?: number | null;
  unit_cost_avg_cents?: number | null;
  /** What the flagged drones' parts cost the run. Financial only. */
  flagged_materials_cents?: number | null;
}

export interface NewWorkOrderInput {
  model_code?: string;
  output_part_id?: number;
  quantity: number;
  variant?: string | null;
  planned_date?: string | null;
  sales_order_id?: string | number | null;
  notes?: string | null;
}

export interface WorkOrderPatch {
  quantity?: number;
  variant?: string | null;
  planned_date?: string | null;
  notes?: string | null;
}

export interface ListFilter {
  status?: WorkOrderStatus;
  model?: string;
  q?: string;
}

/** A box named by its sticker or by the item inside it. */
export type BoxRef = { barcode: string } | { receipt_item_id: string | number };

export type RemovalDestination = 'stock' | 'qc_hold' | 'scrap';

export type RemovalTarget = { board_id: number | string } | { part_instance_id: string };

/**
 * One component fitted to a drone, as the scanner's assembly read returns it
 * (`GET /scanner/assembly/:serial`, keyed by slot). A board's `id` is the
 * board row; an instance's is the part_instances uuid.
 */
export interface FittedPart {
  source: 'board' | 'instance';
  id: number | string;
  label: string;
  value: string;
  display_name?: string | null;
}

export interface RemovalResult {
  unit: Unit;
  removed: unknown;
  destination: RemovalDestination;
}

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

const base = (id: string) => `/work-orders/${encodeURIComponent(id)}`;

export function useWorkOrdersApi() {
  const api = useApi();
  return {
    list: (filter: ListFilter = {}): Promise<WorkOrderRow[]> => {
      const qs = new URLSearchParams();
      if (filter.status) qs.set('status', filter.status);
      if (filter.model) qs.set('model', filter.model);
      if (filter.q) qs.set('q', filter.q);
      const s = qs.toString();
      return api.get(`/work-orders${s ? `?${s}` : ''}`);
    },

    inbox: (): Promise<InboxRow[]> => api.get('/work-orders/inbox'),

    get: (id: string): Promise<WorkOrderDetail> => api.get(base(id)),

    create: (input: NewWorkOrderInput): Promise<WorkOrderDetail> =>
      api.post('/work-orders', { body: input }),

    update: (id: string, patch: WorkOrderPatch): Promise<WorkOrderDetail> =>
      api.patch(base(id), { body: patch }),

    approve: (id: string): Promise<WorkOrderDetail> => api.post(`${base(id)}/approve`, { body: {} }),

    release: (id: string): Promise<WorkOrderDetail> => api.post(`${base(id)}/release`, { body: {} }),

    hold: (id: string, reason?: string): Promise<WorkOrderDetail> =>
      api.post(`${base(id)}/hold`, { body: reason ? { reason } : {} }),

    resume: (id: string, reason?: string): Promise<WorkOrderDetail> =>
      api.post(`${base(id)}/resume`, { body: reason ? { reason } : {} }),

    cancel: (id: string, reason: string): Promise<WorkOrderDetail> =>
      api.post(`${base(id)}/cancel`, { body: { reason } }),

    close: (id: string, note?: string): Promise<WorkOrderDetail> =>
      api.post(`${base(id)}/close`, { body: note ? { note } : {} }),

    /** The one pool: every counted run's outstanding need against Assembly. */
    assemblyPickList: (): Promise<AssemblyPickList> => api.get('/assembly/pick-list'),

    assemblyPick: (box: BoxRef): Promise<PickResult> => api.post('/assembly/picks', { body: box }),

    assemblyBoxEmpty: (box: BoxRef, note: string): Promise<BoxEmptyResult> =>
      api.post('/assembly/box-empty', { body: { ...box, note } }),

    coverage: (id: string): Promise<Coverage> => api.get(`${base(id)}/coverage`),

    units: (id: string): Promise<Unit[]> => api.get(`${base(id)}/units`),

    /** Released and in-progress runs, oldest released first. */
    floor: (): Promise<FloorRun[]> => api.get('/work-orders/floor'),

    check: (id: string, serial: string): Promise<UnitCheck> =>
      api.get(`${base(id)}/units/by-serial/${encodeURIComponent(serial)}/check`),

    // `operatorId` is the person signed in at a Scanner station; absent on the
    // run page, where the server credits the signed-in user.
    completeOne: (id: string, unitId: string, source: 'scan', operatorId?: string): Promise<Unit> =>
      api.post(`${base(id)}/units/${encodeURIComponent(unitId)}/complete`, {
        body: operatorId ? { source, operator_id: operatorId } : { source },
      }),

    flag: (id: string, unitId: string, reason: string, operatorId?: string): Promise<Unit> =>
      api.post(`${base(id)}/units/${encodeURIComponent(unitId)}/flag`, {
        body: operatorId ? { reason, operator_id: operatorId } : { reason },
      }),

    requestPicks: (id: string, input: PickRequestInput): Promise<PickRequest[]> =>
      api.post(`${base(id)}/pick-requests`, { body: input }),

    cancelPickRequest: (id: string, requestId: string, operatorId?: string): Promise<PickRequest> =>
      api.post(`${base(id)}/pick-requests/${encodeURIComponent(requestId)}/cancel`, {
        body: operatorId ? { operator_id: operatorId } : {},
      }),

    removal: (
      id: string,
      unitId: string,
      body: RemovalTarget & { destination: RemovalDestination; reason: string; operator_id?: string },
    ): Promise<RemovalResult> =>
      api.post(`${base(id)}/units/${encodeURIComponent(unitId)}/removal`, { body }),

    /** Not a work order route: the scanner's read of what a drone carries. */
    fittedParts: (serial: string): Promise<{ fitted?: Record<string, FittedPart> }> =>
      api.get(`/scanner/assembly/${encodeURIComponent(serial)}`),

    events: (id: string): Promise<WorkOrderEvent[]> => api.get(`${base(id)}/events`),

    closeReport: (id: string): Promise<CloseReport> => api.get(`${base(id)}/close-report`),
  };
}

export type WorkOrdersApi = ReturnType<typeof useWorkOrdersApi>;

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const STATUS_LABELS: Record<WorkOrderStatus, string> = {
  suggested: 'Suggested',
  draft: 'Draft',
  released: 'Released',
  in_progress: 'In progress',
  complete: 'Complete',
  closed: 'Closed',
  on_hold: 'On hold',
  cancelled: 'Cancelled',
};

export const UNIT_STATUS_LABELS: Record<UnitStatus, string> = {
  in_progress: 'In progress',
  complete: 'Complete',
  flagged: 'Flagged',
};

export const COMPLETION_SOURCE_LABELS: Record<CompletionSource, string> = {
  count: 'End-of-day count',
  bulk: 'Marked built',
  scan: 'Scanned',
  commissioned: 'Commissioned',
  repair: 'Finished in repair',
};

export const DESTINATION_LABELS: Record<RemovalDestination, string> = {
  stock: 'Stock',
  qc_hold: 'QC hold',
  scrap: 'Scrap',
};

/** Statuses the Open tab shows. */
export const OPEN_STATUSES: WorkOrderStatus[] = ['released', 'in_progress', 'on_hold'];
export const DONE_STATUSES: WorkOrderStatus[] = ['complete', 'closed'];

// ---------------------------------------------------------------------------
// Reading helpers
// ---------------------------------------------------------------------------

export function workOrderErrorCode(err: unknown): string | null {
  const data = (err as { data?: { error?: { code?: string } } } | null)?.data;
  return data?.error?.code ?? null;
}

/**
 * A sentence for the person. The server's own message wins where it sent one;
 * otherwise the code says what to do next.
 */
export function workOrderErrorMessage(err: unknown, fallback = 'That did not save. Try again.'): string {
  // The one code whose sentence is ours rather than the server's: it tells the bench where the drone goes.
  if (workOrderErrorCode(err) === 'unit_flagged') return 'This drone was flagged off the run. Take it to repair.';
  const data = (err as { data?: { error?: { message?: string } } } | null)?.data;
  if (data?.error?.message) return data.error.message;
  switch (workOrderErrorCode(err)) {
    case 'not_editable':
      return 'This work order is already released, so its quantity and dates are fixed. Cancel it and raise a new one to change them.';
    case 'no_active_bom':
      return 'This product has no current parts list. Start one on its Product BOMs page, then release.';
    case 'bom_empty':
      return 'The parts list for this product is empty. Add its parts, then release.';
    case 'box_not_found':
      return 'No box has that barcode. Check the sticker and scan again.';
    case 'box_empty':
      return 'That box is already empty. Scan the next box for this part.';
    case 'already_in_assembly':
      return 'That box is already in Assembly. Nothing moved.';
    case 'no_assembly':
      return 'No location is tagged as Assembly yet. An admin switches one on in Locations.';
    case 'not_in_a_warehouse':
      return 'That box is not in a warehouse, so it cannot be picked. Put it away first.';
    case 'order_not_released':
      return 'This run is not approved yet. A manager approves it first.';
    case 'legacy_box':
      return 'Legacy boxes are for reflashing, not for runs.';
    case 'serial_in_other_order':
      return 'That drone already belongs to another work order. Check the serial.';
    case 'wrong_model':
      return 'That drone is a different model from this run. Check the serial.';
    case 'order_full':
      return 'Every unit on this work order already has a drone.';
    case 'unit_not_found':
      return 'That drone is not on this run yet. Scan its parts onto it first.';
    case 'traced_missing':
      return 'A part is not scanned onto this drone yet. Scan it, then check again.';
    case 'parts_short':
      return 'Assembly is short a part for this drone. Request a box.';
    default: {
      const message = (err as { message?: string } | null)?.message;
      return message && !message.includes('[object') && !/^HTTP \d/.test(message) ? message : fallback;
    }
  }
}

/** "4.00" to "4", "0.50" to "0.5". Display only. */
export function formatQty(q: string | number | null | undefined): string {
  if (q === null || q === undefined || q === '') return '—';
  const n = typeof q === 'number' ? q : Number(q);
  if (!Number.isFinite(n)) return String(q);
  return String(parseFloat(n.toFixed(4)));
}

/** "F021, Gen2 ublox": the model code, then its name when it has one. */
export function modelReading(o: Pick<WorkOrderRow, 'model_code' | 'model_name'>): string {
  if (!o.model_code) return o.model_name || '';
  return o.model_name ? `${o.model_code}, ${o.model_name}` : o.model_code;
}

/** A traced line's fitted part as the bench reads it: the board UID or the sticker's name. */
export function componentReading(c: CheckComponent): string | null {
  if (!c) return null;
  if (typeof c === 'string') return c;
  return c.display_name || c.label || c.value || null;
}
